import { memo, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';
import {
  useLocalizedDate,
  useFilterState,
  useWorkspaceMemberSuggestions,
} from '@/hooks';
import { useGeoMarkupStore } from '@/store';
import { FilterPillsList } from '@/components/ui/FilterPill';
import { resolveMemberLabels } from '@/utils';
import type { PillData } from '@/components/ui/FilterPill';

interface TaskFilterPillsProps {
  readonly showSearchPill?: boolean;
  readonly hideStatusPill?: boolean;
}

export const TaskFilterPills = memo(function TaskFilterPills({
  showSearchPill = false,
  hideStatusPill = false,
}: TaskFilterPillsProps) {
  const { t } = useTranslation();
  const { formatDateCompact } = useLocalizedDate();
  const { getStatusLabel, getPriorityLabel } = useFilterState();

  const status = useGeoMarkupStore(
    useShallow((state) => state.taskFilters.status)
  );
  const priority = useGeoMarkupStore(
    useShallow((state) => state.taskFilters.priority)
  );
  const assignedToId = useGeoMarkupStore(
    useShallow((state) => state.taskFilters.assignedToId)
  );
  const dateFrom = useGeoMarkupStore((state) => state.taskFilters.dateFrom);
  const dateTo = useGeoMarkupStore((state) => state.taskFilters.dateTo);
  const search = useGeoMarkupStore((state) => state.taskFilters.search);
  const location = useGeoMarkupStore((state) => state.taskFilters.location);

  const setSearch = useGeoMarkupStore((state) => state.taskFilters.setSearch);
  const setStatus = useGeoMarkupStore((state) => state.taskFilters.setStatus);
  const setPriority = useGeoMarkupStore(
    (state) => state.taskFilters.setPriority
  );
  const setAssignedToId = useGeoMarkupStore(
    (state) => state.taskFilters.setAssignedToId
  );
  const setDateRange = useGeoMarkupStore(
    (state) => state.taskFilters.setDateRange
  );
  const setLocation = useGeoMarkupStore(
    (state) => state.taskFilters.setLocation
  );

  // Only subscribe to the member list when an assignee filter is active — when
  // empty the assignee pill is never rendered, so the subscription/comparison
  // is pure overhead on the common (no-assignee-filter) path.
  const { members } = useWorkspaceMemberSuggestions({
    enabled: assignedToId.length > 0,
  });

  // Resolve the selected assignee ids to member display names for the pill.
  // Fallback to the id only if the member can't be resolved (never in practice —
  // the Select offers active members), so the pill is never empty.
  const assigneeLabel = useMemo(
    () => resolveMemberLabels(members, assignedToId),
    [members, assignedToId]
  );

  const pills = useMemo(() => {
    const result: PillData[] = [];

    if (showSearchPill && search) {
      result.push({
        id: 'search',
        label: t('taskViews.list.filters.title'),
        value: search,
        onRemove: () => setSearch(''),
      });
    }

    if (!hideStatusPill && status.length > 0) {
      const firstStatus = status[0];
      const statusValue =
        status.length === 1 && firstStatus
          ? getStatusLabel(firstStatus)
          : status.map((statusItem) => getStatusLabel(statusItem)).join(', ');

      result.push({
        id: 'status',
        label: t('filters.statusLabel'),
        value: statusValue,
        onRemove: () => setStatus([]),
      });
    }

    if (priority.length > 0) {
      result.push({
        id: 'priority',
        label: t('filters.priorityLabel'),
        value: priority
          .map((priorityItem) => getPriorityLabel(priorityItem))
          .join(', '),
        onRemove: () => setPriority([]),
      });
    }

    if (assignedToId.length > 0) {
      result.push({
        id: 'assignee',
        label: t('filters.assignee'),
        value: assigneeLabel ?? assignedToId.join(', '),
        onRemove: () => setAssignedToId([]),
      });
    }

    if (dateFrom || dateTo) {
      const fromText = dateFrom
        ? formatDateCompact(dateFrom)
        : t('filters.none');
      const toText = dateTo ? formatDateCompact(dateTo) : t('filters.none');

      result.push({
        id: 'date-range',
        label: t('filters.dateRange'),
        value: `${fromText} - ${toText}`,
        onRemove: () => setDateRange(null, null),
      });
    }

    if (location.length > 0) {
      result.push({
        id: 'location',
        label: t('filters.location'),
        value: location,
        onRemove: () => setLocation(''),
      });
    }

    return result;
  }, [
    showSearchPill,
    hideStatusPill,
    search,
    status,
    priority,
    assignedToId,
    assigneeLabel,
    dateFrom,
    dateTo,
    location,
    t,
    formatDateCompact,
    getStatusLabel,
    getPriorityLabel,
    setSearch,
    setStatus,
    setPriority,
    setAssignedToId,
    setDateRange,
    setLocation,
  ]);

  return <FilterPillsList pills={pills} />;
});
